import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { Box, Link, Typography, Alert, Snackbar } from '@mui/material';
import { AuthUserConfirmation } from '../../components';
import { useAppDispatch, useAppSelector } from '../../redux';
import { updateEmail, userSelectors } from '../../redux/user';
import {
  authSelectors,
  authUser,
  clearAuthError,
  setAuthUserEmailSent,
  setSubmitProfileEmailSent,
} from '../../redux/auth';
import { usePersistUserAnswers, useSkipEmailVerification } from '../../hooks';
import { isStatusLoading } from '../../utils';
import { EditOrResendEmailBlock } from '../profileReview/components';
import { validationSchema } from './components/validationSchema';
import SignInDialog from './components/SignInDialog';
import ConsentTermsDialog from 'src/components/ConsentTermsDialog/ConsentTermsDialog';
import { LpSignInConsent } from '../../features/termsOfUse/LpSignInConsent';
import { magicLinkCandidateSelectors } from '../../redux/magicLinkCandidate';
import { positionsSelectors } from '../../redux/positions';
import { massCampaignCandidateSelectors } from '../../redux/massCampaignCandidate';
import { logEvent } from 'src/services';
import { candidateSourceSelectors } from 'src/redux/candidateSource';
import { SIGN_UP_PROVIDERS, getGoogleOAuthErrorMessage } from '../../constants';
import { InitialButtons } from './components/InitialButtons';
import { EmailFormFields } from './components/EmailFormFields';
import { GoogleConsentScreen } from './components/GoogleConsentScreen';
import { useSearchParams } from 'react-router-dom';

export const EmailForm: React.FC<{ title: string | ReactNode }> = ({
  title,
}) => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [googleOAuthError, setGoogleOAuthError] = React.useState<string | null>(
    null,
  );

  const userData = useAppSelector(userSelectors.getUserData);
  const zohoId = useAppSelector(
    magicLinkCandidateSelectors.getMagicLinkCandidateZohoId,
  );
  const position = useAppSelector(positionsSelectors.getPositionData);
  const massCampaignData = useAppSelector(
    massCampaignCandidateSelectors.getMassCampaignCandidateData,
  );
  const candidateSource = useAppSelector(
    candidateSourceSelectors.getCandidateSourceData,
  );
  const isEmailLoading = useAppSelector(
    userSelectors.getIsUserPerformingAction,
  );
  const authApiStatus = useAppSelector(authSelectors.getAuthApiStatus);
  const isAuthUserEmailSent = useAppSelector(
    authSelectors.getIsAuthUserEmailSent,
  );

  const [savedEmail, setSavedEmail] =
    usePersistUserAnswers<Record<string, string>>('emailStep');

  const [isOpenSignInDialog, setIsOpenSignInDialog] = useState(false);
  const [isOpenConsentTermsDialog, setIsOpenConsentTermsDialog] =
    useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showGoogleConsent, setShowGoogleConsent] = useState(false);

  const shouldSkipEmailVerification = useSkipEmailVerification();

  const isGoogleOAuthUser =
    userData?.signupProvider === SIGN_UP_PROVIDERS.GOOGLE;

  const formik = useFormik({
    initialValues: {
      email: userData?.email
        ? userData.email
        : savedEmail?.email
        ? savedEmail.email
        : '',
      isConsentChecked: isGoogleOAuthUser || (savedEmail?.email ? true : false),
    },
    validationSchema,
    onSubmit: async (values) => {
      logEvent('launchpod-email-continue-click', {
        'Email Unconfirmed': formik.values.email,
      });

      if (userData) {
        dispatch(updateEmail({ ...values }));
        setSavedEmail({ email: formik.values.email });
      } else {
        dispatch(
          authUser({
            ...values,
            zohoId,
            position,
            massCampaignData,
            candidateSource,
          }),
        );
      }

      dispatch(setSubmitProfileEmailSent(false));
      dispatch(clearAuthError());
    },
  });

  useEffect(() => {
    logEvent('launchpod-email-page-loaded');

    const authError = searchParams.get('authError');
    if (authError) {
      const errorMessage = getGoogleOAuthErrorMessage(authError);
      setGoogleOAuthError(errorMessage);
      searchParams.delete('authError');
      searchParams.delete('utm_medium');
      setSearchParams(searchParams, { replace: true });
    }

    return () => {
      dispatch(setAuthUserEmailSent(false));
      dispatch(clearAuthError());
    };
  }, []);

  useEffect(() => {
    setSavedEmail({ email: formik.values.email });
  }, [formik.values.email]);

  const sendEmailAction = useCallback(
    async (email: string) => {
      if (userData) {
        return await dispatch(updateEmail({ email })).unwrap();
      } else {
        return await dispatch(
          authUser({
            email,
            zohoId,
            position,
            massCampaignData,
            candidateSource,
          }),
        ).unwrap();
      }
    },
    [userData, zohoId, position, massCampaignData, candidateSource],
  );

  const handleLoginClick = () => {
    setIsOpenSignInDialog(true);
  };

  const handleModalClose = () => {
    setIsOpenSignInDialog(false);
  };

  const handleContinueWithEmail = () => {
    setShowEmailForm(true);
  };

  const handleSignInWithGoogle = () => {
    setShowGoogleConsent(true);
  };

  const handleGoogleSignInConfirm = () => {
    setShowGoogleConsent(false);
  };

  const handleBackFromGoogleConsent = () => {
    setShowGoogleConsent(false);
  };

  const handleOtherOptions = () => {
    setShowEmailForm(false);
  };

  const handleOpenConsentTerms = () => {
    setIsOpenConsentTermsDialog(true);
  };

  return isAuthUserEmailSent && !shouldSkipEmailVerification ? (
    <>
      <AuthUserConfirmation email={formik.values.email} />
      <EditOrResendEmailBlock
        userEmail={formik.values.email}
        sendEmailAction={sendEmailAction}
        emailChangeAction={() => dispatch(setAuthUserEmailSent(false))}
        isLoading={isStatusLoading(authApiStatus) || isEmailLoading}
      />
    </>
  ) : showGoogleConsent ? (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
      }}
    >
      <GoogleConsentScreen
        onBack={handleBackFromGoogleConsent}
        onGoogleSignIn={handleGoogleSignInConfirm}
        isLoading={isStatusLoading(authApiStatus) || isEmailLoading}
      />
    </Box>
  ) : (
    <Box>
      <Typography
        variant="h2"
        mb={4}
        sx={(theme) => ({
          [theme.breakpoints.down('sm')]: {
            mb: 2,
          },
          fontWeight: 600,
        })}
      >
        {title}
      </Typography>

      {!showEmailForm && !isGoogleOAuthUser && !userData ? (
        <InitialButtons
          onContinueWithEmail={handleContinueWithEmail}
          onSignInWithGoogle={handleSignInWithGoogle}
          isLoading={isStatusLoading(authApiStatus) || isEmailLoading}
        />
      ) : (
        <EmailFormFields
          email={formik.values.email}
          isConsentChecked={formik.values.isConsentChecked}
          onEmailChange={(email) => formik.setFieldValue('email', email)}
          onConsentChange={(checked) =>
            formik.setFieldValue('isConsentChecked', checked)
          }
          onSubmit={formik.handleSubmit}
          onOtherOptions={handleOtherOptions}
          onOpenConsentTerms={handleOpenConsentTerms}
          isLoading={isStatusLoading(authApiStatus) || isEmailLoading}
          emailError={formik.errors.email}
          consentError={formik.errors.isConsentChecked}
          emailTouched={formik.touched.email}
          consentTouched={formik.touched.isConsentChecked}
          isGoogleOAuthUser={isGoogleOAuthUser}
          hideOtherOptions={!!userData}
        />
      )}

      {!userData?.email && (
        <Box display="flex" gap={1} alignItems="center" mt={2}>
          <Typography variant="body1">Applied before?</Typography>
          <Link
            onClick={handleLoginClick}
            role="button"
            tabIndex={0}
            sx={{ cursor: 'pointer' }}
          >
            Log in
          </Link>
        </Box>
      )}
      <ConsentTermsDialog
        isOpen={isOpenConsentTermsDialog}
        handleClose={() => setIsOpenConsentTermsDialog(false)}
        handleConfirm={() => setIsOpenConsentTermsDialog(false)}
        title="Consent on personal data processing"
        dialogProps={{ maxWidth: '800px' }}
      >
        <LpSignInConsent />
      </ConsentTermsDialog>
      <SignInDialog
        isOpen={isOpenSignInDialog}
        handleClose={handleModalClose}
        initialValue={savedEmail?.email}
      />
      <Snackbar
        open={!!googleOAuthError}
        autoHideDuration={8000}
        onClose={() => setGoogleOAuthError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setGoogleOAuthError(null)}
          severity="error"
          sx={{ width: '100%' }}
        >
          {googleOAuthError}
        </Alert>
      </Snackbar>
    </Box>
  );
};
